import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { StatusDot } from "@/components/ui/status-dot";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bot,
  Loader2,
  MoreHorizontal,
  Plus,
  Trash2,
} from "lucide-react";
import * as React from "react";
import {
  listPavilionAgentMcpServersQueryOptions,
  useAddMcpServer,
  useRemoveMcpServer,
} from "../../lib/hooks/usePavilionAgents";

// MCP Server data structure - matches the API response
interface McpServer {
  id: string;
  name: string;
  server_url: string;
  auth_url: string | null;
  state: "authenticating" | "connecting" | "ready" | "discovering" | "failed";
  instructions: string | null;
  capabilities: {
    tools?: string[];
    resources?: string[];
    prompts?: string[];
  } | null;
}

// Single server item component
interface MCPServerListItemProps {
  server: McpServer;
  onRemove: (serverId: string) => void;
}

function MCPServerListItem({ server, onRemove }: MCPServerListItemProps) {
  const [isRemoving, setIsRemoving] = React.useState(false);

  const handleRemove = async () => {
    setIsRemoving(true);
    try {
      await onRemove(server.id);
    } catch (error) {
      console.error("Failed to remove MCP server:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  const needsAuth = server.state === "authenticating";
  const statusColorMap: Record<McpServer["state"], string> = {
    authenticating: "bg-warning",
    connecting: "bg-info",
    discovering: "bg-secondary",
    failed: "bg-destructive",
    ready: "bg-success",
  };
  const pulsingStates: McpServer["state"][] = [
    "connecting",
    "authenticating",
    "discovering",
  ];

  return (
    <div className="flex items-center gap-3 rounded-lg bg-muted/50 py-2 px-3 border">
      <Tooltip>
        <TooltipTrigger asChild>
          <StatusDot
            status={server.state}
            colorMap={statusColorMap}
            pulse={pulsingStates.includes(server.state)}
            ariaLabel={`${server.name} status: ${server.state}`}
          />
        </TooltipTrigger>
        <TooltipContent
          className={
            server.state === "failed"
              ? "flex items-center gap-2 border-red-900 bg-red-950 text-red-400"
              : ""
          }
        >
          {server.state === "failed" ? (
            <>
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <p>Connection failed</p>
            </>
          ) : (
            <p>
              {server.state.charAt(0).toUpperCase() + server.state.slice(1)}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
      <div className="flex-1 min-w-0">
        <div className="truncate text-sm font-medium">{server.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {server.server_url}
        </div>
        {server.capabilities && (
          <div className="flex gap-2 mt-1">
            {server.capabilities.tools &&
              server.capabilities.tools.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground">
                      {server.capabilities.tools.length} tools
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="max-w-xs">
                      <p className="font-semibold text-xs mb-1">
                        Available Tools:
                      </p>
                      <p className="text-xs">
                        {server.capabilities.tools.join(", ")}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            {server.capabilities.resources &&
              server.capabilities.resources.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground">
                      {server.capabilities.resources.length} resources
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="max-w-xs">
                      <p className="font-semibold text-xs mb-1">
                        Available Resources:
                      </p>
                      <p className="text-xs">
                        {server.capabilities.resources.join(", ")}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
            {server.capabilities.prompts &&
              server.capabilities.prompts.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground">
                      {server.capabilities.prompts.length} prompts
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="max-w-xs">
                      <p className="font-semibold text-xs mb-1">
                        Available Prompts:
                      </p>
                      <p className="text-xs">
                        {server.capabilities.prompts.join(", ")}
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}
          </div>
        )}
      </div>
      {needsAuth && server.auth_url && (
        <Button
          asChild
          variant="outline"
          className="h-auto shrink-0 px-3 py-1 text-xs"
        >
          <a href={server.auth_url} target="_blank" rel="noopener noreferrer">
            Authenticate
          </a>
        </Button>
      )}
      <FpDropdownMenu>
        <FpDropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">MCP Server Options</span>
          </Button>
        </FpDropdownMenuTrigger>
        <FpDropdownMenuContent align="end">
          <FpDropdownMenuItem
            onClick={handleRemove}
            disabled={isRemoving}
            className="text-red-600 hover:!text-red-600 hover:!bg-red-600/10"
          >
            {isRemoving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </>
            )}
          </FpDropdownMenuItem>
        </FpDropdownMenuContent>
      </FpDropdownMenu>
    </div>
  );
}

// Add MCP Server Popover Component
function AddMcpServerPopover({
  onAdd,
}: {
  onAdd: (url: string, name: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [url, setUrl] = React.useState("");
  const [name, setName] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (url && name && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onAdd(url, name);
        setUrl("");
        setName("");
        setOpen(false);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add MCP Server
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start" side="bottom">
        <div className="p-4 space-y-4">
          <div>
            <h4 className="font-semibold text-sm">Add MCP Server</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Connect an MCP (Model Context Protocol) server to provide
              additional tools and capabilities to your agent.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="server-name" className="text-xs">
                Server Name
              </Label>
              <Input
                id="server-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My Knowledge Base"
                required
                disabled={isSubmitting}
                className="h-8 mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                A friendly name to identify this server
              </p>
            </div>
            <div>
              <Label htmlFor="server-url" className="text-xs">
                Server URL
              </Label>
              <Input
                id="server-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://mcp.example.com"
                required
                disabled={isSubmitting}
                className="h-8 mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The MCP server endpoint URL
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!url || !name || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Server"
                )}
              </Button>
            </div>
          </form>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// MCP Servers component
interface McpServersTableProps {
  agentName: string;
}

function McpServersTableComponent({ agentName }: McpServersTableProps) {
  // Fetch MCP servers data directly
  const { data: mcpServersData, isLoading } = useQuery(
    listPavilionAgentMcpServersQueryOptions(agentName),
  );

  // Mutations
  const addMcpServerMutation = useAddMcpServer();
  const removeMcpServerMutation = useRemoveMcpServer();

  // Convert MCP servers from API format
  const mcpServers = React.useMemo(() => {
    if (!mcpServersData?.servers) {
      return [];
    }
    return Object.entries(mcpServersData.servers).map(([id, server]) => ({
      ...server,
      id,
    })) as McpServer[];
  }, [mcpServersData]);

  // Handle add MCP server
  const handleAddMcpServer = async (url: string, name: string) => {
    await addMcpServerMutation.mutateAsync({
      agentName,
      url,
      name,
    });
  };

  // Handle remove MCP server
  const handleRemoveMcpServer = async (serverId: string) => {
    await removeMcpServerMutation.mutateAsync({
      agentName,
      serverId,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold">MCP Servers</h3>
          <p className="text-sm text-muted-foreground">
            Connect Model Context Protocol servers to extend agent capabilities
          </p>
          <div>
            <AddMcpServerPopover onAdd={handleAddMcpServer} />
          </div>
        </div>
      </div>

      {mcpServers.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Bot className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-sm font-semibold">
            No MCP servers connected
          </h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Add an MCP server to provide additional tools and capabilities to
            your agent.
          </p>
        </div>
      ) : (
        <TooltipProvider delayDuration={100}>
          <div className="space-y-2">
            {mcpServers.map((server) => (
              <MCPServerListItem
                key={server.id}
                server={server}
                onRemove={handleRemoveMcpServer}
              />
            ))}
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

export const McpServersTable = React.memo(McpServersTableComponent);
